import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useNavigate } from 'react-router-dom';
import {
  normalizeMfaChallenge,
  type LoginResponse,
  type MfaRequiredPayload,
  type MfaSetupRequiredPayload,
} from '../types/authMfa';
import { SET_USER } from '../utils/constants/action-types';
import { APP_ROUTES } from '../utils/constants/appRoutes';
import { decodeJwtToken } from '../utils/helpers/validateuser';
import { getSafeRedirectPath } from '../utils/redirectAfterLogin';
import { setToken } from '../utils/security/tokenStorage';

export const useAzureLogin = (
  setLoadingAzure: (loading: boolean) => void,
  setError: (message: string) => void,
  redirectPath: string = APP_ROUTES.HOME,
  onMfaRequired?: (payload: MfaRequiredPayload) => void,
  onMfaSetupRequired?: (payload: MfaSetupRequiredPayload) => void
) => {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
  const navigate = useNavigate();

  return useCallback(async () => {
    try {
      const response = await instance.loginPopup({
        scopes: ['email', 'openid'],
      });

      setLoadingAzure(true);
      try {
        const azureResponse = await axios
          .create({
            baseURL: API_ADDRESS,
            headers: { Authorization: `Bearer ${response.idToken}` },
          })
          .post<LoginResponse>('api/auth/validate-azure', {});

        const data = azureResponse.data;

        if ('mfaRequired' in data && data.mfaRequired) {
          onMfaRequired?.({
            mfaContextToken: data.mfaContextToken,
            mfaChallenge: normalizeMfaChallenge(data.mfaChallenge),
          });
          return;
        }

        if ('mfaSetupRequired' in data && data.mfaSetupRequired && data.accessToken) {
          onMfaSetupRequired?.({ accessToken: data.accessToken });
          return;
        }

        if ('accessToken' in data && data.accessToken) {
          try {
            setToken(data.accessToken);
            const userData = decodeJwtToken(data.accessToken);
            dispatch({ type: SET_USER, payload: userData });
            const safePath = getSafeRedirectPath(redirectPath, APP_ROUTES.HOME);
            navigate(safePath);
          } catch (tokenError) {
            console.error('[AzureLogin] Failed to store token securely:', tokenError);
            setError('Failed to store authentication token. Please try again.');
          }
          return;
        }

        setError('Unexpected response from Entra sign-in. Please try again.');
      } catch (error: any) {
        console.error(error);
        setError(
          error.response?.data?.message ||
            'Cannot login with Entra. Please contact the administrator.'
        );
      } finally {
        setLoadingAzure(false);
      }
    } catch (error: any) {
      console.error(error);
      setError(
        error.message.includes('user_cancelled')
          ? 'Entra authentication cancelled.'
          : 'Cannot login with Entra. Please contact the administrator.'
      );
    }
  }, [
    API_ADDRESS,
    dispatch,
    instance,
    navigate,
    onMfaRequired,
    onMfaSetupRequired,
    redirectPath,
    setError,
    setLoadingAzure,
  ]);
};
